class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton?.addEventListener(
      'click',
      this.clear.bind(this)
    );

    this.input?.addEventListener(
      'input',
      FoxTheme.utils.debounce(
        this.onChange.bind(this),
        300
      )
    );

    this.input?.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.searchContent =
      this.querySelector('.search__content');

    this.searchRecommendationEmpty =
      this.dataset.searchRecommendationEmpty === 'true';

    this.header =
      document.querySelector('.header-section header');

    this.toggleNavigationButton =
      document.querySelector('.toggle-navigation-button');


    /*
     * IMPORTANT
     *
     * On ne teste PAS :
     *
     * this.closest('header')
     *
     * car la page /search possède elle aussi
     * un <header> sémantique.
     *
     * Seul le moteur situé dans la vraie
     * section Shopify du header doit déclencher
     * l'overlay global.
     */

    this.isHeaderSearch =
      this.closest('.header-section') !== null;


    this.boundHandleClickOutside =
      this.handleClickOutside.bind(this);

    document.addEventListener(
      'click',
      this.boundHandleClickOutside
    );


    this.searchProductTypes?.addEventListener(
      'change',
      this.handleProductTypeChange.bind(this)
    );


    document.addEventListener(
      'menu-drawer:open',
      () => {
        this.classList.remove(
          'predictive-search-open'
        );

        if (this.isHeaderSearch) {
          document.body.classList.remove(
            'search-open'
          );
        }
      }
    );


    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }


  /* =========================================================
     GETTERS
     ========================================================= */

  get input() {
    return this.querySelector(
      'input[type="search"]'
    );
  }


  get resetButton() {
    return this.querySelector(
      'button[type="reset"]'
    );
  }


  get searchProductTypes() {
    return this.querySelector(
      '#SearchProductTypes'
    );
  }


  /* =========================================================
     FOCUS
     ========================================================= */

  onFocus(event) {

    document.documentElement.style.setProperty(
      '--viewport-height',
      `${window.innerHeight}px`
    );


    if (this.header) {

      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(
          this.header.getBoundingClientRect().bottom
        )}px`
      );

    }


    /*
     * Overlay global uniquement
     * pour la recherche du header.
     */

    if (
      this.isHeaderSearch &&
      !this.searchRecommendationEmpty
    ) {

      document.body.classList.add(
        this.states.SEARCH_OPEN
      );

      this.toggleNavButtonVisibility(false);

    }


    if (!this.searchRecommendationEmpty) {

      this.searchContent?.classList.remove(
        'hidden'
      );

    }


    this.classList.add(
      this.states.OPEN
    );


    /*
     * Champ vide :
     * recommandations éventuelles uniquement.
     */

    if (this.getQuery().length === 0) {

      if (this.searchRecommendationEmpty) {

        this.searchContent?.classList.add(
          'hidden'
        );

      }

      return;
    }


    /*
     * Une recherche existe déjà :
     * on recharge le predictive search.
     */

    const url =
      this.setupURL().toString();

    this.renderSection(
      url,
      event
    );
  }


  /* =========================================================
     QUERY
     ========================================================= */

  getQuery() {

    return this.input
      ? this.input.value.trim()
      : '';

  }


  /* =========================================================
     RESET
     ========================================================= */

  clear(event = null) {

    event?.preventDefault();

    if (!this.input) return;


    this.input.value = '';

    this.removeAttribute(
      'results'
    );


    /*
     * Fermeture avant refocus pour éviter
     * le maintien d'un ancien résultat.
     */

    this.toggleSearchState(false);

    this.input.focus();
  }


  /* =========================================================
     TYPE PRODUIT
     ========================================================= */

  handleProductTypeChange() {

    const query =
      this.getQuery();


    if (query.length > 0) {

      const url =
        this.setupURL().toString();

      this.renderSection(url);

    }

  }


  /* =========================================================
     URL SHOPIFY
     ========================================================= */

  setupURL() {

    const url = new URL(
      `${window.shopUrl}${FoxTheme.routes.predictive_search_url}`
    );


    let searchTerm =
      this.getQuery();


    if (
      this.searchProductTypes &&
      this.searchProductTypes.value !== ''
    ) {

      searchTerm =
        `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(searchTerm)}`;

    }


    url.searchParams.set(
      'q',
      searchTerm
    );


    /*
     * Limite définie dans predictive-search.liquid.
     *
     * Header : réglage thème
     * Page search : 6
     */

    url.searchParams.set(
      'resources[limit]',
      this.dataset.resultsLimit || 3
    );


    url.searchParams.set(
      'resources[limit_scope]',
      'each'
    );


    url.searchParams.set(
      'section_id',
      FoxTheme.utils.getSectionId(this)
    );


    return url;
  }


  /* =========================================================
     CHANGE
     ========================================================= */

  onChange() {

    if (
      this.getQuery().length === 0
    ) {

      this.clear();

      return;

    }


    const url =
      this.setupURL().toString();


    this.renderSection(url);
  }


  /* =========================================================
     RENDER
     ========================================================= */

  renderSection(url) {

    if (
      this.cachedMap.has(url)
    ) {

      this.renderSectionFromCache(url);

    } else {

      this.renderSectionFromFetch(url);

    }

  }


  renderSectionFromCache(url) {

    const responseText =
      this.cachedMap.get(url);


    this.renderSearchResults(
      responseText
    );


    this.setAttribute(
      'results',
      ''
    );
  }


  renderSectionFromFetch(url) {

    this.setLoadingState(true);


    fetch(url)

      .then((response) => {

        if (!response.ok) {

          throw new Error(
            'Network response was not ok'
          );

        }

        return response.text();

      })

      .then((responseText) => {

        this.renderSearchResults(
          responseText
        );


        this.cachedMap.set(
          url,
          responseText
        );

      })

      .catch((error) => {

        console.error(
          'Error fetching predictive search data:',
          error
        );


        this.setAttribute(
          'error',
          'Failed to load data'
        );

      })

      .finally(() => {

        this.setLoadingState(false);

      });

  }


  /* =========================================================
     INJECTION DES RÉSULTATS
     ========================================================= */

  renderSearchResults(responseText) {

    const sectionId =
      FoxTheme.utils.getSectionId(this);


    const id =
      `PredictiveSearchResults-${sectionId}`;


    const targetElement =
      document.getElementById(id);


    if (!targetElement) {

      console.error(
        `Element with id '${id}' not found in the document.`
      );

      return;
    }


    const parser =
      new DOMParser();


    const parsedDoc =
      parser.parseFromString(
        responseText,
        'text/html'
      );


    const contentElement =
      parsedDoc.getElementById(id);


    if (!contentElement) {

      console.error(
        `Element with id '${id}' not found in predictive search response.`
      );

      return;
    }


    targetElement.innerHTML =
      contentElement.innerHTML;


    this.searchContent?.classList.remove(
      'hidden'
    );


    this.classList.add(
      this.states.OPEN
    );


    /*
     * Le body.search-open est réservé
     * au moteur du header principal.
     */

    if (this.isHeaderSearch) {

      document.body.classList.add(
        this.states.SEARCH_OPEN
      );

      this.toggleNavButtonVisibility(false);

    }
  }


  /* =========================================================
     CLICK OUTSIDE
     ========================================================= */

  handleClickOutside(event) {

    const target =
      event.target;


    let shouldClose =
      false;


    if (this.isHeaderSearch) {

      shouldClose =
        !this.contains(target) &&
        (
          (
            target.classList.contains(
              'fixed-overlay'
            ) &&
            target.closest(
              '.header-section'
            )
          ) ||
          target.classList.contains(
            'header__search-close'
          )
        );

    } else {

      shouldClose =
        !this.contains(target);

    }


    if (shouldClose) {

      setTimeout(() => {

        this.toggleSearchState(false);

      });

    }
  }


  /* =========================================================
     OPEN / CLOSE
     ========================================================= */

  toggleSearchState(isOpen) {

    this.classList.toggle(
      this.states.OPEN,
      isOpen
    );


    if (this.isHeaderSearch) {

      document.body.classList.toggle(
        this.states.SEARCH_OPEN,
        isOpen
      );


      this.toggleNavButtonVisibility(
        !isOpen
      );

    }


    if (
      !isOpen &&
      this.searchRecommendationEmpty
    ) {

      this.searchContent?.classList.add(
        'hidden'
      );

    }
  }


  /* =========================================================
     NAVIGATION HEADER
     ========================================================= */

  toggleNavButtonVisibility(isOpen) {

    if (!this.toggleNavigationButton) {
      return;
    }


    this.toggleNavigationButton.classList.toggle(
      'is-show',
      isOpen
    );
  }


  /* =========================================================
     LOADING
     ========================================================= */

  setLoadingState(isLoading) {

    if (isLoading) {

      this.setAttribute(
        'loading',
        'true'
      );


      this.resetButton?.classList.add(
        this.states.LOADING
      );

    } else {

      this.removeAttribute(
        'loading'
      );


      this.resetButton?.classList.remove(
        this.states.LOADING
      );


      this.setAttribute(
        'results',
        'true'
      );

    }
  }
}


if (
  !customElements.get(
    'predictive-search'
  )
) {

  customElements.define(
    'predictive-search',
    PredictiveSearch
  );

}